"use client";

import { type AlertRootProps, Button, cn } from "@pharaday/lighthouse";
import { useEffect, useState } from "react";

import useCountdown from "../../hooks/useCountdown";

const COUNTDOWN_FROM = 60;

export interface RequestCodeAgainButtonProps {
  onRequest: () => Promise<unknown>;
  onAlert: (alert?: { type: AlertRootProps["type"]; message: string }) => void;
  successMessage?: string;
}

const RequestCodeAgainButton = ({
  onRequest,
  onAlert,
  successMessage = "We sent you a new verification code",
}: RequestCodeAgainButtonProps) => {
  const [isPending, setIsPending] = useState(false);
  const countdown = useCountdown(COUNTDOWN_FROM);

  useEffect(() => {
    countdown.start();

    return () => {
      countdown.cleanup();
      onAlert(undefined);
    };
  }, []);

  const handleClick = async () => {
    onAlert(undefined);
    setIsPending(true);

    try {
      await onRequest();
      countdown.start();
      onAlert({ type: "success", message: successMessage });
    } catch {
      onAlert({ type: "error", message: "We couldn't send a new code. Please try again." });
    } finally {
      setIsPending(false);
    }
  };

  return (
    <Button
      color="primary"
      variant="link"
      className={cn("underline", isPending && "cursor-wait!")}
      type="button"
      isLoading={isPending}
      disabled={Boolean(countdown.count)}
      onClick={handleClick}
    >
      Request again {countdown.count ? `(${countdown.count}s)` : ""}
    </Button>
  );
};

export default RequestCodeAgainButton;
